/**
 * ImportPreview — the Assistant's side-by-side file import view.
 *
 * SOURCE: for a DOCUMENT (PDF, picture, Word) two views — the FILE itself as
 * it was uploaded, and the rows the AI READ from it. For a data file, and in
 * the "Read by AI" view, the attached file as it arrived — the spreadsheet column letter and
 * the target each mapped column goes to, a line gutter striped with what will
 * happen to the record the line belongs to (new / edited / needs a fix), and
 * the selected record's lines highlighted. Clicking a line opens that record
 * in the grid; selecting a record in the grid scrolls the source to its lines.
 *
 * GRID: the object's OWN multi-tabular spreadsheet — the same grid the Run
 * view uses — seeded with the records the server prepared: new records in add
 * mode, existing ones in edit mode (stored values underneath, the file's on
 * top), child grids per record, and every cell the server could not cleanse
 * already marked. A change trail tells a change the FILE made from one the
 * user made. The grid's own Save All commits each record atomically through
 * the engine, so validations, business logic, audit and the user's rights all
 * apply; nothing here writes data itself.
 *
 * REVIEW PANEL: what still needs a fix (live — it empties as cells are
 * corrected), what is worth a check, what the import handled on its own, and
 * the MATCHES — every lookup cell whose code came from a name, a description
 * or the other party's own code, with the closest candidates to switch to (a
 * pick writes the cell as a typed edit would, so its item_change fills the
 * rest). After Save All the kept and picked matches are remembered — in the
 * column's cross-reference, or as the master's aliases — so the next file
 * resolves them exactly.
 *
 * The seed goes in through `EmbedDocStateContext.initialRecords` — the bridge
 * smart documents use to restore unsynced rows — and `autoRetrieve={false}`
 * keeps the table's other rows out of the preview.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertCircle, AlertTriangle, Check, CheckCircle2, ChevronsLeft, ChevronsRight, Download, FilePlus, FileSearch,
  FileText, Info, Link2, MoreVertical, RotateCcw, Table2, X, ZoomIn, ZoomOut,
} from 'lucide-react'
import type { EmbedConfig } from '@twasta/page-schema'
import EmbedCaptureTabularPreview, {
  kindOf as recordKind, needsFix as recordNeedsFix, TRAIL_TONE as CHANGE_TRAIL_TONE,
  type ChangeTrail, type TabularReviewHandle, type TabularReviewOptions,
} from '../EmbedCaptureTabularPreview'
import { EmbedDocStateContext, type EmbedDocState, type EmbedRecord } from '../useEmbedRecords'
import type { CaptureRuntime } from '../contract'
import { LayerLiftContext } from '../../../layers'
import type { SplitScrollHandle } from '../../SplitScrollTable'
import {
  importErrorInfo, type ImportErrorInfo, type ImportIssue, type ImportLearnBody, type ImportLearnItem,
  type ImportLearnResult, type ImportMatch, type ImportOutcome,
  type ImportPreviewData as AssistantImportPreview, type ImportOriginal,
} from './types'
import { PdfPagesView, type PdfFocus } from './PdfPagesView'

function _isImgVal(s: string): boolean {
  const t = s.trim()
  return /^https?:\/\/.+\.(?:jpg|jpeg|png|gif|webp|avif)(?:[?#].*)?$/i.test(t)
    || t.startsWith('/api/website/')
}

// ── View preferences ────────────────────────────────────────────────

interface Prefs {
  /** Where the source pane sits, or hidden. */
  side: 'left' | 'right' | 'hidden'
  /** Source pane width, px. */
  srcW: number
  panel: boolean
  trail: ChangeTrail
  dense: boolean
  hideSame: boolean
  openChanged: boolean
  /** Scroll the source to the lines of the record selected in the grid. */
  sync: boolean
  /** A document's source view: the uploaded file, or the rows read from it. */
  srcView: 'file' | 'rows'
  /** Review panel width, px, and whether it is folded to a strip. */
  panelW: number
  panelMin: boolean
  /** Keep these for the next import (browser storage). */
  remember: boolean
}

const DEFAULT_PREFS: Prefs = {
  side: 'left', srcW: 460, panel: true, trail: 'inline', dense: false,
  hideSame: false, openChanged: false, sync: true, srcView: 'file', remember: true,
  panelW: 300, panelMin: false,
}
const PREFS_KEY = 'twasta:import-preview:prefs'
const SRC_MIN = 260
const GRID_MIN = 480
const PANEL_MIN = 240
const PANEL_MAX = 720

function loadPrefs(): Prefs {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY)
    if (!raw) return DEFAULT_PREFS
    const saved = JSON.parse(raw) as Partial<Prefs>
    return { ...DEFAULT_PREFS, ...saved, remember: true }
  } catch (err) {
    console.warn('[ImportPreview] could not read saved view preferences; using defaults', err)
    return DEFAULT_PREFS
  }
}

function storePrefs(p: Prefs) {
  try {
    if (p.remember) window.localStorage.setItem(PREFS_KEY, JSON.stringify(p))
    else window.localStorage.removeItem(PREFS_KEY)
  } catch (err) {
    console.warn('[ImportPreview] could not save view preferences', err)
  }
}

// ── Helpers ─────────────────────────────────────────────────────────

/** 1 → A, 26 → Z, 27 → AA — the letter a spreadsheet shows over the column. */
function colLetter(n: number): string {
  let s = ''
  let x = n
  while (x > 0) {
    const r = (x - 1) % 26
    s = String.fromCharCode(65 + r) + s
    x = Math.floor((x - 1) / 26)
  }
  return s || '?'
}

function lineSpan(lines: number[]): string {
  if (lines.length === 0) return 'no lines'
  const lo = Math.min(...lines)
  const hi = Math.max(...lines)
  return lo === hi ? `line ${lo}` : `lines ${lo}–${hi}`
}

/** Line gutter stripe per record state. */
const STRIPE = {
  fix: '#d92d20', new: '#2f5fe3', edited: '#d98a12', saved: '#059669',
} as const

interface ReviewItem {
  key: string
  who: string
  what: string
  line?: number
  recId?: string
  /** The cell the item is about: its field (form key), its form (null = the
   *  header) and, for a line, the row's `_localId`. Clicking goes there. */
  field?: string
  formNo?: string | null
  rowId?: string | null
  /** The field's on-screen name, shown above the message. */
  fieldLabel?: string
}

/** `"2.item_code"` → `{formNo: "2", key: "item_code"}`; a header key as is. */
function splitTarget(t: string): { formNo: string | null; key: string } {
  const m = /^(\d+)\.(.+)$/.exec(t)
  return m ? { formNo: m[1], key: m[2] } : { formNo: null, key: t }
}

/** One lookup cell the import resolved — or could not — from a written value. */
interface MatchEntry {
  key: string
  recId: string
  formNo: string | null
  rowId: string | null
  field: string
  match: ImportMatch
  /** auto = the import chose; pick = the person has to. */
  kind: 'auto' | 'pick'
  line?: number
}

type ReviewTab = 'fix' | 'check' | 'matches' | 'handled'

const METHOD_TEXT: Record<string, string> = {
  display: 'by name',
  xref: "by their own code",
  alias: 'by a remembered alias',
  vector: 'by description',
  text: 'by description',
}

/** Only these are worth remembering — an exact name, cross-reference or alias
 *  hit already resolves exactly next time. */
const LEARN_METHODS = new Set(['vector', 'text'])

function matchEntries(prepared: NonNullable<AssistantImportPreview['prepared']>): MatchEntry[] {
  const out: MatchEntry[] = []
  const add = (recId: string, formNo: string | null, rowId: string | null,
    meta: Record<string, ImportMatch> | undefined, kind: 'auto' | 'pick', line?: number) => {
    for (const [field, match] of Object.entries(meta || {})) {
      if (!match || typeof match !== 'object') continue
      out.push({
        key: `${recId}:${formNo ?? ''}:${rowId ?? ''}:${field}:${kind}`,
        recId, formNo, rowId, field, match, kind, line,
      })
    }
  }
  for (const r of prepared.records) {
    const line = (r.sourceRows || [])[0]
    add(r.id, null, null, r.resolved, 'auto', line)
    add(r.id, null, null, r.candidates, 'pick', line)
    for (const [formNo, rows] of Object.entries(r.details || {})) {
      for (const row of rows || []) {
        const rowId = String((row as Record<string, unknown>)._localId ?? '')
        if (!rowId) continue
        add(r.id, formNo, rowId, (row as { _resolved?: Record<string, ImportMatch> })._resolved, 'auto', line)
        add(r.id, formNo, rowId, (row as { _candidates?: Record<string, ImportMatch> })._candidates, 'pick', line)
      }
    }
  }
  return out
}

/** The live value of an entry's cell, or undefined when its row is gone. */
function cellValue(rec: EmbedRecord | undefined, e: MatchEntry): unknown {
  if (!rec) return undefined
  if (!e.formNo) return rec.header?.[e.field]
  const rows = (rec.details?.[e.formNo] as Array<Record<string, unknown>> | undefined) || []
  const row = rows.find((x) => String(x._localId ?? '') === e.rowId)
  return row ? row[e.field] : undefined
}

function cellRow(rec: EmbedRecord | undefined, e: MatchEntry): Record<string, unknown> | undefined {
  if (!rec || !e.formNo) return undefined
  const rows = (rec.details?.[e.formNo] as Array<Record<string, unknown>> | undefined) || []
  return rows.find((x) => String(x._localId ?? '') === e.rowId)
}

const sameText = (a: unknown, b: unknown) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase()

// ── Component ───────────────────────────────────────────────────────

export function ImportPreview({
  project, importId, objLabel, onClose, fetchPreview, fetchFile, captureRuntime, versionKey,
  describeError, onLearn, onImportAnother,
}: {
  project: string
  importId: string
  objLabel?: string
  onClose: (outcome: ImportOutcome | null) => void
  /** Finish this file (exactly like Close — learning, the outcome) and go
   *  back to choose another one. Without it only Close is offered. */
  onImportAnother?: (outcome: ImportOutcome | null) => void
  /** Where the preview body comes from — the host's endpoint for this import
   *  (the Assistant's staged import, or an `import` button's). */
  fetchPreview: () => Promise<AssistantImportPreview>
  /** The uploaded DOCUMENT itself (the host's `…/{import_id}/file`). Without
   *  it a document's source pane shows only the rows read from it. */
  fetchFile?: (page?: number) => Promise<Blob>
  /** The host's capture runtime for the object's grid (data adapter + form
   *  transport + layout persistence). Save All goes through it. */
  captureRuntime: CaptureRuntime
  /** Remount key for a model change (the platform's object-version store). */
  versionKey?: unknown
  /** Read a refused load's reason. Defaults to `importErrorInfo`. */
  describeError?: (e: unknown, fallback: string) => ImportErrorInfo
  /** Remember kept / picked matches of the saved records (the host's
   *  `…/{import_id}/learn`). Without it "Remember these matches" is not offered. */
  onLearn?: (body: ImportLearnBody) => Promise<ImportLearnResult>
}) {
  const [data, setData] = useState<AssistantImportPreview | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs)
  const [narrow, setNarrow] = useState(() => window.innerWidth < 900)
  const [menuOpen, setMenuOpen] = useState(false)
  const [frozen, setFrozen] = useState(false)
  const [tab, setTab] = useState<ReviewTab>('fix')
  const [rememberMatches, setRememberMatches] = useState(true)
  // Every record the grid holds, reported after each change.
  const [live, setLive] = useState<EmbedRecord[] | null>(null)
  const [sel, setSel] = useState<{ id: string | null; from: 'grid' | 'src'; n: number }>(
    { id: null, from: 'grid', n: 0 },
  )
  const [activeLine, setActiveLine] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  // Unsynced records reported by the grid after each change (see EmbedDocState).
  const unsyncedRef = useRef<EmbedRecord[] | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const srcScrollRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<TabularReviewHandle | null>(null)
  const splitRef = useRef<SplitScrollHandle | null>(null)

  const setPrefs = useCallback((patch: Partial<Prefs>) => {
    setPrefsState((p) => {
      const next = { ...p, ...patch }
      storePrefs(next)
      return next
    })
  }, [])

  // Read through a ref: a caller's inline loader is a new function every
  // render, and the preview must load once per import, not once per render.
  const fetchRef = useRef(fetchPreview)
  fetchRef.current = fetchPreview
  useEffect(() => {
    let cancelled = false
    fetchRef.current()
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => {
        if (cancelled) return
        console.warn('[ImportPreview] could not load the preview', e)
        setErr((describeError ?? importErrorInfo)(e, 'The preview could not be loaded.').message)
      })
    return () => { cancelled = true }
  }, [project, importId])

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const prepared = data?.prepared || null
  const total = prepared?.records.length || 0
  // Records that had something to save. One matched but identical to what is
  // stored arrives 'retrieved' — never unsynced, so it must not count as saved.
  const toSaveIds = useMemo(
    () => new Set((prepared?.records || []).filter((r) => r.status !== 'retrieved').map((r) => r.id)),
    [prepared],
  )

  // Seeded records the user has left out of the import (dropped from the
  // grid). Read through a ref by close(), which must see the latest set.
  const leftOutRef = useRef<Set<string>>(new Set())

  // How far Save All has got: records of the file still unsaved, and saved.
  const [saveState, setSaveState] = useState<{ remaining: number; saved: number }>(
    { remaining: -1, saved: 0 })

  const finishWith = useCallback((done: (outcome: ImportOutcome | null) => void) => {
    const left = unsyncedRef.current
    if (!prepared || left === null) { done(null); return }
    // Only rows the file brought in count — a row the user added by hand
    // is not part of what the file asked for.
    const mine = left.filter((r) => toSaveIds.has(r.id))
    const failed = mine.filter((r) => r.status === 'failed' || r.status === 'partial').length
    const pending = mine.length - failed
    const leftIds = new Set(mine.map((r) => r.id))
    const dropped = [...toSaveIds].filter((id) => leftOutRef.current.has(id)).length
    // A record neither still pending nor left out went through Save All.
    const savedIds = [...toSaveIds].filter((id) => !leftIds.has(id) && !leftOutRef.current.has(id))
    const saved = savedIds.length
    learnRef.current(new Set(savedIds))
    done({
      total: toSaveIds.size, saved, failed, pending, unchanged: total - toSaveIds.size,
      ...(dropped ? { leftOut: dropped } : {}),
    })
  }, [prepared, total, toSaveIds])

  const close = useCallback(() => finishWith(onClose), [finishWith, onClose])
  const importAnother = useCallback(() => {
    if (onImportAnother) finishWith(onImportAnother)
  }, [finishWith, onImportAnother])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (menuOpen) { setMenuOpen(false); return }
      close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close, menuOpen])

  // The grid's seed. Built once per preparation: the hook reads
  // `initialRecords` only on mount. Match provenance stays HERE (the Matches
  // tab reads it from `prepared`) — rows go to the save verbatim.
  const docState = useMemo<EmbedDocState | null>(() => {
    if (!prepared) return null
    const initialRecords: EmbedRecord[] = prepared.records.map((r) => ({
      id: r.id,
      status: r.status,
      ...(r.primaryKey ? { primaryKey: r.primaryKey } : {}),
      header: { ...r.header },
      ...(r.originalHeader ? { originalHeader: { ...r.originalHeader } } : {}),
      details: Object.fromEntries(Object.entries(r.details || {}).map(([fno, rows]) => [
        fno, (rows || []).map((row) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _resolved, _candidates, ...rest } = JSON.parse(JSON.stringify(row))
          return rest
        }),
      ])),
      fieldErrors: { ...(r.fieldErrors || {}) },
      recordError: r.recordError,
    }))
    unsyncedRef.current = initialRecords
    return {
      initialRecords,
      onRecordsChange: (recs) => {
        unsyncedRef.current = recs
        const left = new Set(recs.filter((r) => toSaveIds.has(r.id)).map((r) => r.id))
        const saved = [...toSaveIds].filter((id) => !left.has(id) && !leftOutRef.current.has(id)).length
        setSaveState({ remaining: left.size, saved })
      },
    }
  }, [prepared, toSaveIds])

  const embed = useMemo<EmbedConfig | null>(() => (prepared ? {
    obj_name: prepared.obj_name, obj_type: 'T', mode: 'full', filters: [],
    renderMode: 'capture', captureTemplate: 'multi-tabular',
  } as EmbedConfig : null), [prepared])

  // ── Record ↔ source line maps ──────────────────────────────────
  const recIndex = useMemo(() => {
    const m = new Map<string, number>()
    prepared?.records.forEach((r, i) => m.set(r.id, i))
    return m
  }, [prepared])
  const linesOf = useMemo(() => {
    const m = new Map<string, number[]>()
    prepared?.records.forEach((r) => m.set(r.id, r.sourceRows || []))
    return m
  }, [prepared])
  const recOfLine = useMemo(() => {
    const m = new Map<number, string>()
    prepared?.records.forEach((r) => (r.sourceRows || []).forEach((l) => m.set(l, r.id)))
    return m
  }, [prepared])
  const liveById = useMemo(() => new Map((live || []).map((r) => [r.id, r])), [live])
  // Seeded records the user has left out of the import (dropped from the grid).
  const leftOut = useMemo(() => {
    const s = new Set<string>()
    if (!live) return s
    prepared?.records.forEach((r) => { if (!liveById.has(r.id)) s.add(r.id) })
    return s
  }, [prepared, live, liveById])
  leftOutRef.current = leftOut

  const recLabel = useCallback((id: string): string => {
    const r = liveById.get(id)
    const i = recIndex.get(id)
    return r?.primaryKey || (i != null ? `Record ${i + 1}` : 'New record')
  }, [liveById, recIndex])

  // A field's on-screen name — "Email", not "email"; a line field as
  // "<form>.<key>" (the server's labels), else the key made readable.
  const labelOf = useCallback((target: string): string => {
    const l = prepared?.labels?.[target]
    if (l) return l
    const { key } = splitTarget(target)
    return prepared?.labels?.[key] || key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  }, [prepared])

  const selLines = useMemo(
    () => new Set(sel.id ? linesOf.get(sel.id) || [] : []),
    [sel.id, linesOf],
  )

  // What the PDF page view marks, and when it scrolls: a pick in the grid (or
  // the review panel) while following; a click on the page itself only marks.
  const pdfSeq = useRef(0)
  const lastSelN = useRef(-1)
  if (sel.n !== lastSelN.current) {
    lastSelN.current = sel.n
    if (sel.from === 'grid' && prefs.sync) pdfSeq.current = sel.n
  }
  const pdfFocus = useMemo<PdfFocus | null>(
    () => (sel.id && !leftOut.has(sel.id) ? { lines: linesOf.get(sel.id) || [], seq: pdfSeq.current } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sel, leftOut, linesOf],
  )

  // The screen's name as the menu shows it ("Ticket Booking"), never its
  // object name — the server's title first, then the host's label.
  const objTitle = (prepared?.obj_title && prepared.obj_title !== prepared.obj_name ? prepared.obj_title : '')
    || objLabel || prepared?.obj_title || prepared?.obj_name || ''

  // Scroll the source so the given line sits a quarter of the way down.
  const scrollSourceTo = useCallback((line: number) => {
    const box = srcScrollRef.current
    const el = box?.querySelector<HTMLElement>(`[data-line="${line}"]`)
    if (!box || !el) return
    try {
      const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop
      box.scrollTo({ top: Math.max(0, top - 52 - box.clientHeight / 4), behavior: 'smooth' })
    } catch (e) {
      console.warn('[ImportPreview] could not scroll the source', e)
    }
  }, [])

  // Grid → source: follow the selection when the user picked it in the grid.
  useEffect(() => {
    if (!prefs.sync || sel.from !== 'grid' || !sel.id) return
    const lines = linesOf.get(sel.id)
    if (lines && lines.length) scrollSourceTo(Math.min(...lines))
  }, [sel, prefs.sync, linesOf, scrollSourceTo])

  // Source / review panel → grid: select, open and scroll to the record.
  const focusRecord = useCallback((id: string, from: 'grid' | 'src') => {
    if (leftOut.has(id)) return
    setSel((s) => ({ id, from, n: s.n + 1 }))
    setActiveLine(null)
    gridRef.current?.focusRecord(id)
  }, [leftOut])

  const review = useMemo<TabularReviewOptions>(() => ({
    changeTrail: prefs.trail,
    density: prefs.dense ? 'compact' : 'comfortable',
    hideUnchanged: prefs.hideSame,
    openChangedDetails: prefs.openChanged,
    selectable: true,
    removeLabel: 'Leave out of this import',
    rowMenuItems: prefs.side === 'hidden' ? (rec) => [{
      label: 'Show in source',
      icon: <FileSearch size={14} />,
      onClick: () => {
        setPrefs({ side: 'left' })
        setSel((s) => ({ id: rec.id, from: 'grid', n: s.n + 1 }))
      },
    }] : (rec) => [{
      label: 'Show in source',
      icon: <FileSearch size={14} />,
      onClick: () => setSel((s) => ({ id: rec.id, from: 'grid', n: s.n + 1 })),
    }],
    onRecordsView: setLive,
    onSelectRecord: (rec) => setSel((s) => ({ id: rec?.id ?? null, from: 'grid', n: s.n + 1 })),
  }), [prefs.trail, prefs.dense, prefs.hideSame, prefs.openChanged, prefs.side, setPrefs])

  // ── Review panel content ──────────────────────────────────────
  const fixItems = useMemo<ReviewItem[]>(() => {
    const out: ReviewItem[] = []
    for (const r of live || []) {
      if (!recordNeedsFix(r)) continue
      const line = (linesOf.get(r.id) || [])[0]
      const who = recLabel(r.id)
      let n = 0
      if (r.recordError) { out.push({ key: `${r.id}:rec`, who, what: r.recordError, line, recId: r.id }); n++ }
      for (const [f, msg] of Object.entries(r.fieldErrors || {})) {
        const { formNo, key } = splitTarget(f)
        out.push({
          key: `${r.id}:${f}`, who, what: msg, line, recId: r.id,
          field: key, formNo, rowId: null, fieldLabel: labelOf(f),
        })
        n++
      }
      // A line's own cells (the server marks them on the row).
      for (const [formNo, rows] of Object.entries(r.details || {})) {
        ((rows as Array<Record<string, unknown>>) || []).forEach((row, i) => {
          const fe = row._fieldErrors as Record<string, string> | undefined
          const rowId = String(row._localId ?? '')
          for (const [f, msg] of Object.entries(fe || {})) {
            out.push({
              key: `${r.id}:${formNo}:${rowId || i}:${f}`, who: `${who} · line ${i + 1}`, what: msg,
              line, recId: r.id, field: f, formNo, rowId: rowId || null,
              fieldLabel: labelOf(`${formNo}.${f}`),
            })
            n++
          }
        })
      }
      if (n === 0) {
        out.push({ key: `${r.id}:status`, who, what: 'The last save of this record did not go through — open it to see why.', line, recId: r.id })
      }
    }
    // File-level errors no record carries (a header the import could not read…).
    for (const [i, it] of (prepared?.issues || []).entries()) {
      if (it.severity !== 'error' || it.record != null) continue
      out.push({ key: `file:${i}`, who: 'File', what: it.message, line: it.line })
    }
    return out
  }, [live, prepared, linesOf, recLabel, labelOf])

  const issueItems = useCallback((severity: ImportIssue['severity']): ReviewItem[] => {
    const out: ReviewItem[] = []
    for (const [i, it] of (prepared?.issues || []).entries()) {
      if (it.severity !== severity) continue
      const recId = it.record != null ? prepared?.records[it.record]?.id : undefined
      if (recId && leftOut.has(recId)) continue
      const t = it.field ? splitTarget(it.field) : null
      out.push({
        key: `${severity}:${i}`,
        who: recId ? recLabel(recId) : it.line != null ? `Line ${it.line}` : 'File',
        what: it.message,
        line: it.line, recId,
        ...(t ? { field: t.key, formNo: t.formNo, rowId: null, fieldLabel: labelOf(it.field!) } : {}),
      })
    }
    return out
  }, [prepared, leftOut, recLabel, labelOf])
  const checkItems = useMemo(() => issueItems('warning'), [issueItems])
  const handledItems = useMemo(() => issueItems('info'), [issueItems])

  // ── Matches ───────────────────────────────────────────────────
  const matches = useMemo(() => (prepared ? matchEntries(prepared) : []), [prepared])
  const liveMatches = useMemo(
    () => matches.filter((m) => !leftOut.has(m.recId)),
    [matches, leftOut],
  )
  const pickMatch = useCallback(async (e: MatchEntry, code: string) => {
    const ok = await gridRef.current?.setCell(e.recId, e.formNo, e.rowId, e.field, code)
    if (!ok) console.warn('[ImportPreview] could not write the picked code into the grid', e.key)
  }, [])

  // Learning runs once, on close, over the records that went through Save All.
  const learnRef = useRef<(saved: Set<string>) => void>(() => {})
  learnRef.current = (saved: Set<string>) => {
    if (!onLearn || saved.size === 0) return
    const items: ImportLearnItem[] = []
    for (const e of rememberMatches ? matches : []) {
      if (!saved.has(e.recId) || !e.match.learnable) continue
      if (e.kind === 'auto' && !LEARN_METHODS.has(String(e.match.method || ''))) continue
      const rec = liveById.get(e.recId)
      const code = cellValue(rec, e)
      if (code == null || String(code).trim() === '' || sameText(code, e.match.from)) continue
      items.push({
        ...(e.formNo ? { form_no: e.formNo } : {}),
        field: e.field, value: e.match.from, code: String(code),
        header: { ...(rec?.header || {}) },
        ...(e.formNo ? { row: { ...(cellRow(rec, e) || {}) } } : {}),
      })
    }
    // The saved records themselves: what the SENDER's format is learned from
    // (their document kind, and — for a sender with no profile yet — the AI's
    // draft instructions, built from what the person corrected).
    const strip = (o: Record<string, unknown>) =>
      Object.fromEntries(Object.entries(o || {}).filter(([k]) => !k.startsWith('_')))
    const records = [...saved].map((id) => liveById.get(id)).filter(Boolean).slice(0, 50)
      .map((rec) => ({
        header: strip(rec!.header || {}),
        details: Object.fromEntries(Object.entries(rec!.details || {}).map(([fno, rows]) => [
          fno, ((rows as Array<Record<string, unknown>>) || []).map(strip),
        ])),
      }))
    if (!items.length && !records.length) return
    onLearn({ items, records })
      .then((res) => console.info('[ImportPreview] remembered matches / sender format', res))
      .catch((err) => console.warn('[ImportPreview] could not remember the import', err))
  }

  const openItem = useCallback((it: ReviewItem) => {
    if (it.recId && !leftOut.has(it.recId)) {
      // Marked as a grid pick, so the source follows it (when following).
      focusRecord(it.recId, 'grid')
      // Then into the cell itself: a header field directly, a line field
      // once its row is known (an issue naming only a line FORM opens the record).
      if (it.field && (!it.formNo || it.rowId)) {
        const id = it.recId
        void gridRef.current?.focusCell(id, it.formNo ?? null, it.rowId ?? null, it.field)
          .then((ok) => { if (!ok) console.info('[ImportPreview] the cell is not shown in the grid', it.field) })
      }
      return
    }
    if (it.line != null) {
      setActiveLine(it.line)
      scrollSourceTo(it.line)
    }
  }, [leftOut, focusRecord, scrollSourceTo])

  // ── Splitter ──────────────────────────────────────────────────
  const clampSrcW = useCallback((w: number) => {
    const box = bodyRef.current?.getBoundingClientRect()
    const max = box ? Math.max(SRC_MIN, box.width - GRID_MIN) : 900
    return Math.round(Math.min(max, Math.max(SRC_MIN, w)))
  }, [])
  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault()
    const x0 = e.clientX
    const w0 = prefs.srcW
    const flip = prefs.side === 'right' ? -1 : 1
    setDragging(true)
    const move = (ev: PointerEvent) => {
      setPrefsState((p) => ({ ...p, srcW: clampSrcW(w0 + (ev.clientX - x0) * flip) }))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setDragging(false)
      setPrefsState((p) => { storePrefs(p); return p })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  const onSplitKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 64 : 16
    const flip = prefs.side === 'right' ? -1 : 1
    if (e.key === 'ArrowLeft') { e.preventDefault(); setPrefs({ srcW: clampSrcW(prefs.srcW - step * flip) }) }
    if (e.key === 'ArrowRight') { e.preventDefault(); setPrefs({ srcW: clampSrcW(prefs.srcW + step * flip) }) }
  }

  // ── Review panel width (its left edge drags; it grows leftwards) ─
  const clampPanelW = useCallback((w: number) => {
    const box = bodyRef.current?.getBoundingClientRect()
    const max = box ? Math.max(PANEL_MIN, Math.min(PANEL_MAX, box.width - GRID_MIN / 2)) : PANEL_MAX
    return Math.round(Math.min(max, Math.max(PANEL_MIN, w)))
  }, [])
  const startPanelDrag = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    const x0 = e.clientX
    const w0 = prefs.panelW
    setDragging(true)
    const move = (ev: PointerEvent) => {
      setPrefsState((p) => ({ ...p, panelW: clampPanelW(w0 - (ev.clientX - x0)) }))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setDragging(false)
      setPrefsState((p) => { storePrefs(p); return p })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [prefs.panelW, clampPanelW])
  const onPanelKey = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 64 : 16
    if (e.key === 'ArrowLeft') { e.preventDefault(); setPrefs({ panelW: clampPanelW(prefs.panelW + step) }) }
    if (e.key === 'ArrowRight') { e.preventDefault(); setPrefs({ panelW: clampPanelW(prefs.panelW - step) }) }
  }, [prefs.panelW, clampPanelW, setPrefs])

  const openMenu = () => {
    try { setFrozen(!!splitRef.current?.isFrozen?.()) } catch { setFrozen(false) }
    setMenuOpen((v) => !v)
  }
  const toggleFreeze = () => {
    const h = splitRef.current
    if (!h) return
    try {
      if (frozen) h.unfreeze(); else h.freeze(1)
      setFrozen(!frozen)
    } catch (e) {
      console.warn('[ImportPreview] could not change the frozen column', e)
    }
  }

  // ── The uploaded document (the source pane's "File" view) ──────
  const original = data?.file.original && fetchFile ? data.file.original : null
  const srcView: 'file' | 'rows' = original ? prefs.srcView : 'rows'
  const fetchFileRef = useRef(fetchFile)
  fetchFileRef.current = fetchFile
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [fileErr, setFileErr] = useState<string | null>(null)
  // A PDF the server draws is shown page by page; the whole file is not needed.
  const pages = original?.kind === 'pdf' && original.pages?.length ? original.pages : null
  const wantFile = !!original && srcView === 'file' && !pages
  useEffect(() => {
    if (!wantFile || fileUrl || fileErr || !fetchFileRef.current || !original) return
    let cancelled = false
    let made: string | null = null
    fetchFileRef.current()
      .then((blob) => {
        if (cancelled) return
        // Typed from the server's answer; a host that dropped the type gets
        // the one the preview named, so the viewer knows what it holds.
        const typed = blob.type ? blob : new Blob([blob], { type: original.mime })
        made = URL.createObjectURL(typed)
        setFileUrl(made)
      })
      .catch((e) => {
        if (cancelled) return
        console.warn('[ImportPreview] could not load the uploaded file', e)
        setFileErr((describeError ?? importErrorInfo)(e, 'The uploaded file could not be shown.').message)
      })
    return () => { cancelled = true }
  }, [wantFile, fileUrl, fileErr, original, describeError])
  useEffect(() => () => {
    if (fileUrl) {
      try { URL.revokeObjectURL(fileUrl) } catch { /* already gone */ }
    }
  }, [fileUrl])

  const srcShown = !narrow && prefs.side !== 'hidden'
  const srcNarrowShown = narrow && prefs.side !== 'hidden'
  const panelShown = prefs.panel && !narrow

  const keyText = prepared?.primary_key.length ? prepared.primary_key.join(' + ') : ''
  const modeText = !prepared ? ''
    : prepared.mode === 'add' ? 'add new records'
      : prepared.mode === 'edit' ? `update existing records${keyText ? ` by ${keyText}` : ''}`
        : `add or update${keyText ? ` by ${keyText}` : ''}`

  const selNote = sel.id && !leftOut.has(sel.id)
    ? `${recLabel(sel.id)} · ${lineSpan(linesOf.get(sel.id) || [])}`
    : 'Click a line to open its record'

  // The review panel lives inside the grid frame, to the right of the rows.
  const sidePanel = panelShown ? (
    <ReviewPanel
      tab={tab}
      onTab={setTab}
      fix={fixItems}
      check={checkItems}
      handled={handledItems}
      matches={liveMatches}
      liveById={liveById}
      recLabel={recLabel}
      labelOf={labelOf}
      onPick={pickMatch}
      remember={onLearn ? rememberMatches : null}
      onRemember={setRememberMatches}
      trail={prefs.trail}
      onOpen={openItem}
      showLines={!data?.file.document}
      width={prefs.panelW}
      minimized={prefs.panelMin}
      dragging={dragging}
      onMinimize={(min, t) => { if (t) setTab(t); setPrefs({ panelMin: min }) }}
      onResizeStart={startPanelDrag}
      onResizeKey={onPanelKey}
    />
  ) : null
  const reviewOpts = useMemo(() => ({ ...review, sidePanel }), [review, sidePanel])

  const sourcePane = data && prepared ? (
    <SourcePane
      data={data}
      prepared={prepared}
      selLines={selLines}
      activeLine={activeLine}
      selNote={selNote}
      recOfLine={recOfLine}
      liveById={liveById}
      leftOut={leftOut}
      scrollRef={srcScrollRef}
      original={original}
      view={srcView}
      onView={(v) => setPrefs({ srcView: v })}
      fileUrl={fileUrl}
      fileErr={fileErr}
      labelOf={labelOf}
      pdf={pages && fetchFile ? { fetchPage: (n: number) => fetchFile(n), focus: pdfFocus } : null}
      onPickLine={(line) => {
        const id = recOfLine.get(line)
        if (id && !leftOut.has(id)) focusRecord(id, 'src')
        else setActiveLine(line)
      }}
    />
  ) : null

  return createPortal(
    <div style={{ ...overlay, userSelect: dragging ? 'none' : undefined }} role="dialog" aria-label="Import preview">
      <header style={topBar}>
        <Download size={17} color="var(--color-accent, #1d4ed8)" style={{ flexShrink: 0 }} />
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap' }}>
          Import into {objTitle || '…'}
        </h1>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #5e6472)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data?.filename && <span style={fileChip}>{data.filename}</span>}
          {data?.file.sheet ? ` · sheet ${data.file.sheet}` : ''}
          {modeText && ` · ${modeText}`}
          {prepared && ' · nothing is written until you Save All'}
        </span>
        {data?.reading && <ReadingChip reading={data.reading} />}
        <div style={{ flex: 1 }} />
        {onImportAnother && (
          <button onClick={importAnother} style={closeBtn} title="Finish this file and choose another">
            <FilePlus size={14} /> Import another file
          </button>
        )}
        <button onClick={close} style={closeBtn} aria-label="Close preview" title="Close (Esc)">
          <X size={14} /> Close
        </button>
        <button
          onClick={openMenu}
          style={{ ...iconBtn, borderColor: menuOpen ? 'var(--color-text-secondary, #b8bcc4)' : undefined }}
          aria-label="View preferences" title="View preferences"
          aria-haspopup="menu" aria-expanded={menuOpen}
          disabled={!prepared}
        ><MoreVertical size={16} /></button>
      </header>

      {prepared && saveState.remaining === 0 && saveState.saved > 0 && (
        <div role="status" style={savedBar}>
          <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            All {saveState.saved} record{saveState.saved === 1 ? '' : 's'} from {data?.filename || 'the file'} saved.
          </span>
          {onImportAnother && (
            <button onClick={importAnother} style={savedBarPrimary}>
              <FilePlus size={14} /> Import another file
            </button>
          )}
          <button onClick={close} style={closeBtn}>Done</button>
        </div>
      )}

      {err && <div style={{ padding: 20, color: '#b91c1c' }}>{err}</div>}
      {!err && !data && <div style={{ padding: 20, color: 'var(--color-text-secondary, #6b7280)' }}>Loading the preview…</div>}
      {!err && data && !prepared && (
        <div style={{ padding: 20 }}>This file has not been prepared for import yet.</div>
      )}

      {prepared && docState && embed && (
        <main ref={bodyRef} style={{
          flex: 1, minHeight: 0, padding: 12, gap: 0, display: 'flex',
          flexDirection: narrow ? 'column' : prefs.side === 'right' ? 'row-reverse' : 'row',
          background: 'var(--color-bg-secondary, #f5f4f0)',
        }}>
          {srcShown && (
            <>
              <div style={{ width: prefs.srcW, flexShrink: 0, minHeight: 0, display: 'flex' }}>{sourcePane}</div>
              <div
                role="separator" aria-orientation="vertical" aria-label="Resize panes"
                aria-valuenow={prefs.srcW} tabIndex={0}
                onPointerDown={startDrag} onKeyDown={onSplitKey}
                style={splitter}
                title="Drag to resize · arrow keys too"
              >
                <div style={{ width: 4, height: 44, borderRadius: 3, background: dragging ? '#2f5fe3' : 'var(--color-border, #c9cbd1)' }} />
              </div>
            </>
          )}
          {srcNarrowShown && (
            <div style={{ height: '38%', minHeight: 0, display: 'flex', marginBottom: 12 }}>{sourcePane}</div>
          )}

          <section aria-label={`${objTitle} records`} style={{
            flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
            background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e6e5e0)',
            borderRadius: 12, overflow: 'hidden', padding: 8,
          }}>
            <LayerLiftContext.Provider value={300}>
              <EmbedDocStateContext.Provider value={docState}>
                <EmbedCaptureTabularPreview
                  key={importId}
                  project={project}
                  embed={embed}
                  inputs={[]}
                  inputValues={{}}
                  runtime={captureRuntime}
                  versionKey={versionKey}
                  autoRetrieve={false}
                  splitHandle={splitRef}
                  review={reviewOpts}
                  reviewHandle={gridRef}
                />
              </EmbedDocStateContext.Provider>
            </LayerLiftContext.Provider>
          </section>
        </main>
      )}

      {menuOpen && prepared && (
        <>
          <button onClick={() => setMenuOpen(false)} aria-label="Close menu" style={menuScrim} />
          <div role="menu" aria-label="View preferences" style={menuBox}>
            <MenuHeading>Layout</MenuHeading>
            <MenuRadio on={prefs.side === 'left'} onClick={() => setPrefs({ side: 'left' })}>Source on the left</MenuRadio>
            <MenuRadio on={prefs.side === 'right'} onClick={() => setPrefs({ side: 'right' })}>Source on the right</MenuRadio>
            <MenuRadio on={prefs.side === 'hidden'} onClick={() => setPrefs({ side: 'hidden' })}>Hide the source</MenuRadio>
            <MenuToggle on={prefs.panel} onClick={() => setPrefs({ panel: !prefs.panel })}
              hint={narrow ? 'needs a wider window' : undefined}>Review panel</MenuToggle>
            <MenuDivider />
            <MenuHeading>Change trail</MenuHeading>
            <MenuRadio on={prefs.trail === 'off'} onClick={() => setPrefs({ trail: 'off' })} hint="plain cells">Off</MenuRadio>
            <MenuRadio on={prefs.trail === 'markers'} onClick={() => setPrefs({ trail: 'markers' })} hint="corner flag + tooltip">Markers</MenuRadio>
            <MenuRadio on={prefs.trail === 'inline'} onClick={() => setPrefs({ trail: 'inline' })} hint="both values">Old → new</MenuRadio>
            <MenuDivider />
            <MenuHeading>Rows</MenuHeading>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 6px' }}>
              <span style={{ fontSize: 14 }}>Density</span>
              <div role="group" aria-label="Density" style={segGroup}>
                {([['Comfortable', false], ['Compact', true]] as const).map(([label, v]) => (
                  <button key={label} onClick={() => setPrefs({ dense: v })} aria-pressed={prefs.dense === v}
                    style={segBtn(prefs.dense === v)}>{label}</button>
                ))}
              </div>
            </div>
            <MenuToggle on={prefs.hideSame} onClick={() => setPrefs({ hideSame: !prefs.hideSame })}>Hide unchanged records</MenuToggle>
            <MenuToggle on={prefs.openChanged} onClick={() => setPrefs({ openChanged: !prefs.openChanged })}>Open details of changed records</MenuToggle>
            <MenuToggle on={prefs.sync} onClick={() => setPrefs({ sync: !prefs.sync })}>Follow the selection in the source</MenuToggle>
            <MenuDivider />
            <MenuHeading>Columns</MenuHeading>
            <MenuToggle on={frozen} onClick={toggleFreeze}>Freeze the first column</MenuToggle>
            <MenuDivider />
            <button
              role="menuitem" style={{ ...menuItem, color: gridRef.current?.canRevert() ? '#b42318' : 'var(--color-text-secondary, #9aa0aa)' }}
              disabled={!gridRef.current?.canRevert()}
              onClick={() => { setMenuOpen(false); gridRef.current?.revertAll() }}
            >
              <span style={{ width: 16, display: 'flex' }}><RotateCcw size={14} /></span>
              Undo all my edits — back to the file
            </button>
            <label style={rememberRow}>
              <input type="checkbox" checked={prefs.remember} onChange={() => setPrefs({ remember: !prefs.remember })} />
              Remember these for my next import
            </label>
          </div>
        </>
      )}
    </div>,
    document.body,
  )
}

// ── Source pane ─────────────────────────────────────────────────────

function SourcePane({
  data, prepared, selLines, activeLine, selNote, recOfLine, liveById, leftOut, scrollRef, onPickLine,
  original, view, onView, fileUrl, fileErr, labelOf, pdf,
}: {
  data: AssistantImportPreview
  prepared: NonNullable<AssistantImportPreview['prepared']>
  selLines: Set<number>
  activeLine: number | null
  selNote: string
  recOfLine: Map<number, string>
  liveById: Map<string, EmbedRecord>
  leftOut: Set<string>
  scrollRef: React.RefObject<HTMLDivElement | null>
  onPickLine: (line: number) => void
  /** The uploaded document, when the host can show it. */
  original: ImportOriginal | null
  view: 'file' | 'rows'
  onView: (v: 'file' | 'rows') => void
  fileUrl: string | null
  fileErr: string | null
  /** A target's on-screen name ("Ticket class", not "1.ticket_class"). */
  labelOf: (target: string) => string
  /** A PDF the server draws page by page: how to fetch a page, and what to mark. */
  pdf: { fetchPage: (page: number) => Promise<Blob>; focus: PdfFocus | null } | null
}) {
  const file = data.file
  const targetOf = useMemo(() => {
    const m = new Map<number, string>()
    for (const [src, tgt] of Object.entries(prepared.mapping || {})) {
      const n = /^#(\d+)$/.exec(src)
      if (n) m.set(Number(n[1]), tgt)
    }
    return m
  }, [prepared])
  // Lines the server flagged, when no live record speaks for them.
  const lineSev = useMemo(() => {
    const sev = new Map<number, ImportIssue['severity']>()
    for (const it of prepared.issues || []) {
      if (it.line == null || it.severity !== 'error') continue
      sev.set(it.line, 'error')
    }
    return sev
  }, [prepared])

  const stripeOf = (line: number): string => {
    const id = recOfLine.get(line)
    const rec = id ? liveById.get(id) : undefined
    if (!rec) return lineSev.get(line) === 'error' && !(id && leftOut.has(id)) ? STRIPE.fix : 'transparent'
    if (recordNeedsFix(rec)) return STRIPE.fix
    const k = recordKind(rec)
    return k === 'same' ? 'transparent' : STRIPE[k]
  }

  return (
    <section aria-label="Source file" style={pane}>
      <div style={paneHdr}>
        {original ? (
          <div role="tablist" aria-label="Source view" style={segGroup}>
            <button role="tab" aria-selected={view === 'file'} onClick={() => onView('file')}
              title={`The uploaded ${original.kind === 'image' ? 'picture' : 'document'}, as it arrived`}
              style={{ ...segBtn(view === 'file'), display: 'flex', alignItems: 'center', gap: 5 }}>
              <FileText size={13} /> File
            </button>
            <button role="tab" aria-selected={view === 'rows'} onClick={() => onView('rows')}
              title="The rows the AI read from the document — what the import uses"
              style={{ ...segBtn(view === 'rows'), display: 'flex', alignItems: 'center', gap: 5 }}>
              <Table2 size={13} /> Read by AI · {file.total_rows}
            </button>
          </div>
        ) : (
          <>
            <Table2 size={15} color="#166534" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>Source</span>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #5e6472)', whiteSpace: 'nowrap' }}>
              {file.document ? 'read from the document · ' : ''}
              {file.total_rows} row{file.total_rows === 1 ? '' : 's'}
              {file.truncated ? ` · first ${file.rows.length} shown` : ''} · read-only
            </span>
          </>
        )}
        <div style={{ flex: 1 }} />
        {(view === 'rows' || pdf) && <span style={selChip} title={selNote}>{selNote}</span>}
      </div>
      {view === 'file' && original ? (
        pdf && original.pages?.length ? (
          <PdfPagesView
            original={original as ImportOriginal & { pages: Array<{ w: number; h: number }> }}
            fetchPage={pdf.fetchPage}
            focus={pdf.focus}
            recOfLine={recOfLine}
            leftOut={leftOut}
            onPickLine={onPickLine}
          />
        ) : <OriginalView original={original} url={fileUrl} err={fileErr} />
      ) : (
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative' }}>
        <table style={fileTable}>
          <thead>
            <tr>
              <th style={{ ...fth, width: 44, left: 0, zIndex: 3 }} aria-label="Line" />
              {file.columns.map((col) => {
                const tgt = targetOf.get(col.index)
                const isImgCol = (file.image_cols || []).includes(col.index)
                return (
                  <th key={col.index} style={{ ...fth, opacity: tgt ? 1 : 0.6 }}
                    title={`${col.raw || `Column ${colLetter(col.index)}`} → ${tgt ? labelOf(tgt) : 'not imported'}`}>
                    <div style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {colLetter(col.index)}
                      {isImgCol && <span title="Image column" style={{ fontSize: 13 }}>🖼</span>}
                      <span style={{ fontFamily: 'inherit', fontWeight: 400, marginLeft: 2 }}>{col.raw}</span>
                    </div>
                    <div style={{ fontWeight: 500, color: tgt ? 'var(--color-accent, #1d4ed8)' : 'var(--color-text-secondary, #9ca3af)' }}>
                      {tgt ? `→ ${labelOf(tgt)}` : 'not imported'}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {file.rows.map((r, idx) => {
              const on = selLines.has(r.line) || r.line === activeLine
              const id = recOfLine.get(r.line)
              const gone = !!id && leftOut.has(id)
              const firstOfRec = !!id && idx > 0 && recOfLine.get(file.rows[idx - 1].line) !== id
              return (
                <tr
                  key={r.line} data-line={r.line}
                  onClick={() => onPickLine(r.line)}
                  title={gone ? 'Left out of this import' : id ? 'Open this record in the grid' : undefined}
                  style={{
                    background: on ? 'rgba(47,95,227,0.10)' : undefined,
                    borderTop: firstOfRec ? '1px solid var(--color-border, #e2e1dc)' : undefined,
                    cursor: gone ? 'default' : 'pointer',
                    opacity: gone ? 0.45 : 1,
                    textDecoration: gone ? 'line-through' : undefined,
                  }}
                >
                  <td style={{
                    ...ftd, width: 44, position: 'sticky', left: 0, zIndex: 1, textAlign: 'right',
                    fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 13,
                    background: on ? '#2f5fe3' : 'var(--color-bg-secondary, #fafaf8)',
                    color: on ? '#fff' : 'var(--color-text-secondary, #6b7180)',
                    boxShadow: `inset 3px 0 0 ${on ? '#2f5fe3' : gone ? 'transparent' : stripeOf(r.line)}`,
                  }}>{r.line}</td>
                  {r.values.map((v, i) => {
                    const imgCol = (file.image_cols || []).includes(i + 1)
                    const showThumb = imgCol && !!v && _isImgVal(v)
                    return (
                      <td key={i} style={{ ...ftd, opacity: targetOf.has(i + 1) ? 1 : 0.5, height: showThumb ? 44 : undefined, verticalAlign: 'middle' }} title={v}>
                        {showThumb ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <img
                              src={v}
                              alt=""
                              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3, flexShrink: 0, border: '1px solid var(--color-border, #e6e5e0)', background: 'var(--color-bg-secondary, #f5f4f0)' }}
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                            />
                            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #9ca3af)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
                              {v.replace(/^https?:\/\/[^/]+/, '').slice(0, 40) || v.slice(0, 40)}
                            </span>
                          </span>
                        ) : v}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      )}
      {(file.sheets || []).length > 1 && (
        <div role="tablist" aria-label="Sheets" style={sheetBar}>
          {file.sheets.map((s) => {
            const on = s === file.sheet
            return (
              <button
                key={s} role="tab" aria-selected={on} disabled={!on}
                title={on ? 'The sheet being imported' : 'Only one sheet is imported at a time — ask the Assistant to import this one'}
                style={{
                  border: 0, borderRight: '1px solid var(--color-border, #e6e5e0)', padding: '0 14px',
                  background: on ? 'var(--color-bg, #fff)' : 'transparent',
                  color: on ? 'var(--color-text, #15171c)' : 'var(--color-text-secondary, #5e6472)',
                  fontWeight: on ? 600 : 400, fontSize: 13,
                  boxShadow: on ? 'inset 0 2px 0 #166534' : undefined, cursor: on ? 'default' : 'not-allowed',
                }}
              >{s}</button>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ── The uploaded document ───────────────────────────────────────────

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]

/** The file as it was uploaded: a picture (fit to the pane, zoomable), a PDF
 *  in the browser's own viewer, or — for a Word file, which a browser cannot
 *  show — a download. Read-only; the rows come from the "Read by AI" view. */
function OriginalView({ original, url, err }: {
  original: ImportOriginal
  url: string | null
  err: string | null
}) {
  // null = fit the pane's width.
  const [zoom, setZoom] = useState<number | null>(null)
  const muted = { padding: 20, fontSize: 14, color: 'var(--color-text-secondary, #5e6472)' }
  if (err) return <div role="alert" style={{ ...muted, color: '#b42318' }}>{err}</div>
  if (!url) return <div style={muted}>Loading {original.name}…</div>
  if (original.kind === 'pdf') {
    return (
      <iframe title={`Uploaded file ${original.name}`} src={url}
        style={{ flex: 1, minHeight: 0, width: '100%', border: 0, background: '#525659' }} />
    )
  }
  if (original.kind === 'image') {
    const i = zoom == null ? -1 : ZOOMS.indexOf(zoom)
    const step = (d: number) => {
      const cur = zoom == null ? 2 : i
      setZoom(ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, cur + d))])
    }
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderBottom: '1px solid var(--color-border, #e6e5e0)', fontSize: 13 }}>
          <button onClick={() => step(-1)} style={{ ...iconBtn, width: 30, height: 30 }} aria-label="Zoom out" title="Zoom out"><ZoomOut size={15} /></button>
          <button onClick={() => step(1)} style={{ ...iconBtn, width: 30, height: 30 }} aria-label="Zoom in" title="Zoom in"><ZoomIn size={15} /></button>
          <button onClick={() => setZoom(null)} aria-pressed={zoom == null} style={segBtn(zoom == null)}>Fit width</button>
          <button onClick={() => setZoom(1)} aria-pressed={zoom === 1} style={segBtn(zoom === 1)}>100%</button>
          <span style={{ marginLeft: 'auto', color: 'var(--color-text-secondary, #5e6472)' }}>
            {zoom == null ? 'Fit' : `${Math.round(zoom * 100)}%`}
          </span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg-secondary, #eceae4)', padding: 10 }}>
          <img src={url} alt={`Uploaded file ${original.name}`}
            style={zoom == null
              ? { width: '100%', height: 'auto', display: 'block', boxShadow: '0 1px 4px rgba(0,0,0,0.18)', background: '#fff' }
              : { width: 'auto', maxWidth: 'none', zoom, display: 'block', boxShadow: '0 1px 4px rgba(0,0,0,0.18)', background: '#fff' } as React.CSSProperties} />
        </div>
      </div>
    )
  }
  return (
    <div style={{ ...muted, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
      <span>A Word file can't be shown in the browser. Open it to compare with the rows the AI read.</span>
      <a href={url} download={original.name} style={{ color: 'var(--color-accent, #1d4ed8)', fontWeight: 600 }}>
        Download {original.name}
      </a>
    </div>
  )
}

// ── Review panel ────────────────────────────────────────────────────

function ReviewPanel({
  tab, onTab, fix, check, handled, matches, liveById, recLabel, labelOf, onPick, remember, onRemember,
  trail, onOpen, showLines, width, minimized, dragging, onMinimize, onResizeStart, onResizeKey,
}: {
  tab: ReviewTab
  onTab: (t: ReviewTab) => void
  fix: ReviewItem[]
  check: ReviewItem[]
  handled: ReviewItem[]
  matches: MatchEntry[]
  liveById: Map<string, EmbedRecord>
  recLabel: (id: string) => string
  labelOf: (target: string) => string
  onPick: (e: MatchEntry, code: string) => void
  /** null = the host cannot remember matches. */
  remember: boolean | null
  onRemember: (on: boolean) => void
  trail: ChangeTrail
  onOpen: (it: ReviewItem) => void
  /** Show the file's line number on each item (a spreadsheet; a document's
   *  rows were numbered by the reading, not by the file). */
  showLines: boolean
  width: number
  /** Folded to a strip of counts at the grid's edge. */
  minimized: boolean
  dragging: boolean
  /** Fold / unfold; unfolding from a strip entry opens that tab. */
  onMinimize: (min: boolean, tab?: ReviewTab) => void
  onResizeStart: (e: React.PointerEvent) => void
  onResizeKey: (e: React.KeyboardEvent) => void
}) {
  const tabs: Array<{ k: ReviewTab; label: string; n: number; bg: string; fg: string }> = [
    { k: 'fix', label: 'Needs a fix', n: fix.length, bg: fix.length ? 'rgba(217,45,32,0.12)' : 'rgba(22,101,52,0.12)', fg: fix.length ? '#b42318' : '#166534' },
  ]
  if (check.length) tabs.push({ k: 'check', label: 'Check', n: check.length, bg: 'rgba(217,138,18,0.14)', fg: '#a15c07' })
  if (matches.length) tabs.push({ k: 'matches', label: 'Matches', n: matches.length, bg: 'rgba(47,95,227,0.12)', fg: '#1d4ed8' })
  tabs.push({ k: 'handled', label: 'Handled', n: handled.length, bg: 'var(--color-bg-secondary, #f1f0eb)', fg: 'var(--color-text-secondary, #4a4f5a)' })
  const cur: ReviewTab = (tab === 'check' && !check.length) || (tab === 'matches' && !matches.length) ? 'fix' : tab
  const items = cur === 'fix' ? fix : cur === 'check' ? check : cur === 'handled' ? handled : []
  const dot = cur === 'fix' ? '#d92d20' : cur === 'check' ? '#d98a12' : '#6b7280'

  if (minimized) {
    // A strip of what is waiting, read top to bottom; any entry unfolds it.
    const strip = tabs.filter((t) => t.k !== 'handled')
    return (
      <aside aria-label="Review (minimised)" style={{
        width: 44, flexShrink: 0, borderLeft: '1px solid var(--color-border, #e6e5e0)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '6px 0',
        background: 'var(--color-bg, #fff)', minHeight: 0, overflow: 'auto',
      }}>
        <button onClick={() => onMinimize(false)} aria-label="Restore the review panel" title="Restore"
          style={{ ...iconBtn, width: 32, height: 32 }}>
          <ChevronsLeft size={15} />
        </button>
        {strip.map((t) => (
          <button key={t.k} onClick={() => onMinimize(false, t.k)}
            aria-label={`${t.label}: ${t.n}`} title={`${t.label} — ${t.n}`}
            style={{
              writingMode: 'vertical-rl', border: 0, borderRadius: 8, cursor: 'pointer',
              padding: '10px 4px', minWidth: 32, display: 'flex', alignItems: 'center', gap: 6,
              background: t.bg, color: t.fg, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            }}>
            {t.label}
            <span style={{ writingMode: 'horizontal-tb', fontSize: 13, fontWeight: 700 }}>{t.n}</span>
          </button>
        ))}
      </aside>
    )
  }

  return (
    <aside aria-label="Review" style={{
      width, flexShrink: 0, borderLeft: '1px solid var(--color-border, #e6e5e0)', position: 'relative',
      display: 'flex', flexDirection: 'column', background: 'var(--color-bg, #fff)', minHeight: 0,
    }}>
      <div
        role="separator" aria-orientation="vertical" aria-label="Resize the review panel"
        aria-valuenow={width} tabIndex={0}
        onPointerDown={onResizeStart} onKeyDown={onResizeKey}
        title="Drag to resize · arrow keys too"
        style={{ position: 'absolute', left: -5, top: 0, bottom: 0, width: 10, cursor: 'col-resize', zIndex: 2, touchAction: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ width: 3, height: 40, borderRadius: 3, background: dragging ? '#2f5fe3' : 'transparent' }} />
      </div>
      <div style={{ height: 42, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2, padding: '0 6px 0 8px', borderBottom: '1px solid var(--color-border, #e6e5e0)', overflowX: 'auto' }}>
        {tabs.map((t) => {
          const on = cur === t.k
          return (
            <button key={t.k} onClick={() => onTab(t.k)} aria-pressed={on} style={{
              height: 30, padding: '0 8px', border: 0, borderRadius: 7, cursor: 'pointer',
              background: on ? 'var(--color-bg-secondary, #f1f0eb)' : 'transparent',
              color: on ? 'var(--color-text, #15171c)' : 'var(--color-text-secondary, #5e6472)',
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            }}>
              {t.label}
              <span style={{ fontSize: 13, borderRadius: 999, padding: '0 6px', background: t.bg, color: t.fg }}>{t.n}</span>
            </button>
          )
        })}
        <div style={{ flex: 1 }} />
        <button onClick={() => onMinimize(true)} aria-label="Minimise the review panel" title="Minimise"
          style={{ ...iconBtn, width: 28, height: 28, border: 0 }}>
          <ChevronsRight size={15} />
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 6 }}>
        {cur === 'fix' && items.length === 0 && (
          <div style={{ padding: '14px 10px', fontSize: 13, color: '#166534', fontWeight: 500, display: 'flex', gap: 6, alignItems: 'center' }}>
            <CheckCircle2 size={14} /> All clear — every record can be saved.
          </div>
        )}
        {cur === 'handled' && items.length === 0 && (
          <div style={{ padding: '14px 10px', fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }}>
            Nothing needed converting — the file's values went in as they were.
          </div>
        )}
        {cur === 'matches' && matches.map((e) => (
          <MatchRow key={e.key} entry={e} rec={liveById.get(e.recId)} who={recLabel(e.recId)}
            fieldLabel={labelOf(e.formNo ? `${e.formNo}.${e.field}` : e.field)}
            onPick={onPick}
            onOpen={() => onOpen({ key: e.key, who: '', what: '', recId: e.recId, line: e.line })} />
        ))}
        {items.map((it) => {
          const clickable = !!it.recId || it.line != null
          const Icon = cur === 'fix' ? AlertCircle : cur === 'check' ? AlertTriangle : Info
          return (
            <button
              key={it.key}
              onClick={clickable ? () => onOpen(it) : undefined}
              disabled={!clickable}
              style={{ ...issueBtn, cursor: clickable ? 'pointer' : 'default' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-text, #15171c)' }}>
                <Icon size={12} color={dot} style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.who}{it.fieldLabel ? <span style={{ fontWeight: 600, color: dot }}> · {it.fieldLabel}</span> : null}
                </span>
                {showLines && it.line != null && (
                  <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary, #6b7180)', flexShrink: 0 }}>
                    line {it.line}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #3a3d45)', lineHeight: 1.4, paddingLeft: 18 }}>{it.what}</span>
              {it.field && clickable && (
                <span style={{ fontSize: 13, color: 'var(--color-accent, #1d4ed8)', paddingLeft: 18 }}>Go to the cell →</span>
              )}
            </button>
          )
        })}
      </div>
      {cur === 'matches' && remember !== null && (
        <label style={{ ...rememberRow, marginTop: 0, padding: '10px 14px' }}>
          <input type="checkbox" checked={remember} onChange={() => onRemember(!remember)} />
          Remember these matches for the next import
        </label>
      )}
      {trail !== 'off' && cur !== 'matches' && (
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--color-border, #e6e5e0)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }}>
          <LegendSwatch bg={CHANGE_TRAIL_TONE.file.bg} bd={CHANGE_TRAIL_TONE.file.mark}>Changed by the file</LegendSwatch>
          <LegendSwatch bg={CHANGE_TRAIL_TONE.you.bg} bd={CHANGE_TRAIL_TONE.you.mark}>Changed by you</LegendSwatch>
          <LegendSwatch bg="rgba(217,45,32,0.06)" bd="#d92d20">Must fix before it saves</LegendSwatch>
        </div>
      )}
    </aside>
  )
}

const KIND_TEXT: Record<string, string> = {
  digital: 'a digital document', scanned_print: 'a scanned printout', handwritten: 'handwritten',
  mixed: 'a printed form filled in by hand', photo: 'a photo', printout: 'a photographed printout',
}
const HOW_TEXT: Record<string, string> = {
  picked: 'chosen at upload', recognised: 'recognised from the document',
  document: 'from the document', layout: 'by its spreadsheet layout',
}

/** How a DOCUMENT was read: who sent it, what kind of document it is, and
 *  whether the sender's own instructions were followed. */
function ReadingChip({ reading }: { reading: NonNullable<AssistantImportPreview['reading']> }) {
  const s = reading.sender
  const prof = s?.profile
  const parts = [
    s?.code ? `From ${s.display || s.code} (${HOW_TEXT[String(s.how || '').split(',')[0]] || s.how})` : 'Sender not recognised',
    reading.document_kind ? KIND_TEXT[reading.document_kind] || reading.document_kind : '',
    prof?.has_instructions
      ? (prof.status === 'draft' ? 'sender instructions applied (AI draft)' : 'sender instructions applied')
      : (s?.code ? 'no sender instructions yet — learned after you save' : ''),
  ].filter(Boolean)
  const title = [parts.join(' · '), reading.notes ? `Reading notes: ${reading.notes}` : '']
    .filter(Boolean).join('\n')
  return (
    <span title={title} style={{
      fontSize: 13, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', overflow: 'hidden',
      textOverflow: 'ellipsis', maxWidth: 420, minWidth: 0,
      background: prof?.status === 'draft' ? 'rgba(217,138,18,0.14)' : 'rgba(47,95,227,0.10)',
      color: prof?.status === 'draft' ? '#a15c07' : 'var(--color-accent, #1d4ed8)',
    }}>{parts.join(' · ')}</span>
  )
}

function MatchRow({ entry, rec, who, fieldLabel, onPick, onOpen }: {
  entry: MatchEntry
  rec: EmbedRecord | undefined
  who: string
  fieldLabel: string
  onPick: (e: MatchEntry, code: string) => void
  onOpen: () => void
}) {
  const { match } = entry
  const current = cellValue(rec, entry)
  const cur = current == null ? '' : String(current)
  const options = [
    ...(match.code ? [{ code: match.code, display: match.display, score: match.score ?? undefined }] : []),
    ...(match.candidates || []),
  ].filter((c, i, all) => c.code && all.findIndex((x) => x.code === c.code) === i)
  const chosen = options.some((o) => o.code === cur) ? cur : ''
  const settled = !!chosen
  const how = entry.kind === 'auto' ? METHOD_TEXT[String(match.method || '')] || 'matched' : 'needs a pick'
  const pct = (s?: number | null) => (s == null ? '' : ` · ${Math.round(Number(s) * 100)}%`)
  return (
    <div style={{ ...issueBtn, gap: 5, borderBottom: '1px solid var(--color-border, #f0efea)', borderRadius: 0 }}>
      <button onClick={onOpen} style={{ border: 0, background: 'transparent', padding: 0, textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-text, #15171c)' }}>
        <Link2 size={12} color={settled ? '#1d4ed8' : '#d92d20'} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who}</span>
        <span style={{ fontWeight: 400, color: 'var(--color-text-secondary, #6b7180)', whiteSpace: 'nowrap' }}>
          {fieldLabel}
        </span>
      </button>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #3a3d45)', paddingLeft: 18, lineHeight: 1.4 }}>
        “{match.from}” — {how}{entry.kind === 'auto' ? pct(match.score) : ''}
      </span>
      <select
        aria-label={`Code for “${match.from}”`}
        value={chosen}
        onChange={(ev) => { if (ev.target.value) onPick(entry, ev.target.value) }}
        disabled={!rec || options.length === 0}
        style={{ marginLeft: 18, height: 30, fontSize: 13, borderRadius: 6, maxWidth: 'calc(100% - 18px)',
          border: `1px solid ${settled ? 'var(--color-border, #d5d7dc)' : '#d92d20'}`,
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #15171c)' }}
      >
        {!settled && <option value="">{options.length ? 'Choose the record…' : 'No close record — type the code in the grid'}</option>}
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.code}{o.display ? ` — ${o.display}` : ''}{pct(o.score)}
          </option>
        ))}
      </select>
    </div>
  )
}

function LegendSwatch({ bg, bd, children }: { bg: string; bd: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 14, height: 11, borderRadius: 3, background: bg, border: `1px solid ${bd}` }} />
      {children}
    </span>
  )
}

// ── Menu pieces ─────────────────────────────────────────────────────

function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '6px 8px 4px', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--color-text-secondary, #6b7180)' }}>
      {children}
    </div>
  )
}

function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--color-border, #f0efea)', margin: '4px 0' }} />
}

function MenuRadio({ on, onClick, hint, children }: {
  on: boolean; onClick: () => void; hint?: string; children: React.ReactNode
}) {
  return (
    <button role="menuitemradio" aria-checked={on} onClick={onClick} style={menuItem}>
      <span style={{ width: 16, display: 'flex', color: 'var(--color-accent, #1d4ed8)' }}>
        {on && <Check size={14} strokeWidth={2.6} />}
      </span>
      <span style={{ flex: 1 }}>{children}</span>
      {hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7180)' }}>{hint}</span>}
    </button>
  )
}

function MenuToggle({ on, onClick, hint, children }: {
  on: boolean; onClick: () => void; hint?: string; children: React.ReactNode
}) {
  return (
    <button role="menuitemcheckbox" aria-checked={on} onClick={onClick} style={{ ...menuItem, justifyContent: 'space-between' }}>
      <span>{children}{hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7180)', marginLeft: 6 }}>{hint}</span>}</span>
      <span style={{
        width: 30, height: 18, borderRadius: 999, position: 'relative', flexShrink: 0,
        background: on ? 'var(--color-accent, #1d4ed8)' : 'var(--color-border, #d5d7dc)',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: on ? 14 : 2, width: 14, height: 14, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left 0.12s ease',
        }} />
      </span>
    </button>
  )
}

// ── Styles ──────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  // Above the maximized Assistant panel (9600) so it opens from either size.
  position: 'fixed', inset: 0, zIndex: 9700, display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg-primary, #fff)', color: 'var(--color-text-primary, #1f2937)', fontSize: 14,
}
const topBar: React.CSSProperties = {
  height: 52, flexShrink: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12,
  padding: '0 12px 0 18px', borderBottom: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', position: 'relative',
}
const fileChip: React.CSSProperties = {
  fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 13, padding: '1px 6px',
  borderRadius: 5, background: 'var(--color-bg-secondary, #f1f0eb)', color: 'var(--color-text, #15171c)',
}
const savedBar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 18px',
  fontSize: 14, color: '#166534', background: 'rgba(22,101,52,0.08)',
  borderBottom: '1px solid rgba(22,101,52,0.2)',
}
const savedBarPrimary: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', fontSize: 14,
  fontWeight: 500, borderRadius: 8, border: '1px solid var(--color-accent, #2f5fe3)',
  background: 'var(--color-accent, #2f5fe3)', color: '#fff', cursor: 'pointer',
}
const closeBtn: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px 0 10px', fontSize: 14,
  fontWeight: 500, borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', color: 'inherit', cursor: 'pointer', flexShrink: 0,
}
const iconBtn: React.CSSProperties = {
  width: 32, height: 32, flexShrink: 0, borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text-secondary, #4a4f5a)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}
const pane: React.CSSProperties = {
  flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e6e5e0)',
  borderRadius: 12, overflow: 'hidden',
}
const paneHdr: React.CSSProperties = {
  height: 44, flexShrink: 0, boxSizing: 'border-box', padding: '0 12px', display: 'flex',
  alignItems: 'center', gap: 8, borderBottom: '1px solid var(--color-border, #e6e5e0)',
}
const selChip: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-accent, #1d4ed8)', background: 'rgba(47,95,227,0.10)',
  borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  minWidth: 0, maxWidth: '55%',
}
const fileTable: React.CSSProperties = { borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }
const fth: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 2, background: 'var(--color-bg-secondary, #fafaf8)',
  textAlign: 'left', padding: '6px 8px', fontSize: 13, color: 'var(--color-text-secondary, #5e6472)',
  borderBottom: '1px solid var(--color-border, #e6e5e0)', borderLeft: '1px solid var(--color-border, #eeede8)',
  whiteSpace: 'nowrap', fontWeight: 400,
}
const ftd: React.CSSProperties = {
  height: 30, padding: '0 8px', borderBottom: '1px solid var(--color-border, #f4f3ef)', whiteSpace: 'nowrap',
  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
}
const sheetBar: React.CSSProperties = {
  height: 32, flexShrink: 0, display: 'flex', alignItems: 'stretch', fontSize: 13,
  borderTop: '1px solid var(--color-border, #e6e5e0)', background: 'var(--color-bg-secondary, #fafaf8)',
  overflowX: 'auto',
}
const splitter: React.CSSProperties = {
  width: 12, flexShrink: 0, cursor: 'col-resize', display: 'flex', alignItems: 'center',
  justifyContent: 'center', outlineOffset: -2, touchAction: 'none',
}
const issueBtn: React.CSSProperties = {
  width: '100%', border: 0, background: 'transparent', textAlign: 'left', borderRadius: 8,
  padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3,
}
const menuScrim: React.CSSProperties = {
  position: 'absolute', inset: 0, border: 0, background: 'transparent', zIndex: 10, cursor: 'default',
}
const menuBox: React.CSSProperties = {
  position: 'absolute', top: 46, right: 12, zIndex: 11, width: 320, maxHeight: 'calc(100vh - 64px)',
  overflow: 'auto', boxSizing: 'border-box', background: 'var(--color-bg, #fff)',
  border: '1px solid var(--color-border, #e6e5e0)', borderRadius: 12,
  boxShadow: '0 12px 32px rgba(21,23,28,.14), 0 2px 6px rgba(21,23,28,.06)', padding: 8,
  display: 'flex', flexDirection: 'column', gap: 2,
}
const menuItem: React.CSSProperties = {
  width: '100%', minHeight: 32, border: 0, background: 'transparent', borderRadius: 6,
  display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', fontSize: 14,
  color: 'var(--color-text, #15171c)', textAlign: 'left', cursor: 'pointer',
}
const rememberRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 8, fontSize: 13,
  color: 'var(--color-text-secondary, #5e6472)', cursor: 'pointer',
  borderTop: '1px solid var(--color-border, #f0efea)', marginTop: 4,
}
const segGroup: React.CSSProperties = {
  display: 'flex', border: '1px solid var(--color-border, #e6e5e0)', borderRadius: 7, padding: 2, gap: 2,
}
function segBtn(on: boolean): React.CSSProperties {
  return {
    height: 24, padding: '0 9px', borderRadius: 5, border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    background: on ? 'var(--color-text, #15171c)' : 'transparent',
    color: on ? 'var(--color-bg, #fff)' : 'var(--color-text-secondary, #4a4f5a)',
  }
}
